<script setup>
  import { ref, computed } from "vue";
  import { storeToRefs } from "pinia";
  import { Pencil, Plus } from "lucide-vue-next";
  import {
    Table,
    TableHeader,
    TableBody,
    TableHead,
    TableRow,
    TableCell,
    TableEmpty,
  } from "@/components/ui/table";
  import { Button } from "@/components/ui/button";
  import { useCampaignsPivotStore } from "@stores/useCampaignsPivotStore";
  import GroupModal from "./GroupModal.vue";
  import { dimensionImage, dimensionLabel, dimensionValue } from "../pivotDimensions";
  import DimensionThumbnail from "./DimensionThumbnail.vue";

  const props = defineProps({
    data: { type: Object, required: true },
  });

  const store = useCampaignsPivotStore();
  const { surveyTargets } = storeToRefs(store);

  function targetName(targetId) {
    if (!targetId) return null;
    return surveyTargets.value.find((t) => t.id === targetId)?.name ?? null;
  }

  const colHeaders = computed(() => props.data.col_headers || []);
  const rows = computed(() => props.data.rows || []);
  const rowDimensions = computed(() => props.data.rowDimensions || []);
  const groupHeaders = computed(() => colHeaders.value.filter((h) => h.type === "group"));

  const dimColumns = computed(() => rowDimensions.value.map((d) => ({ key: d, label: dimensionLabel(d) })));

  const totalCols = computed(() => dimColumns.value.length + 1 + groupHeaders.value.length + 1);

  const minCpiByGroup = computed(() => {
    const mins = {};
    rows.value.forEach((row) => {
      Object.entries(row.groups || {}).forEach(([gid, g]) => {
        if (g.n > 5 && g.cpi != null && (mins[gid] == null || g.cpi < mins[gid])) {
          mins[gid] = g.cpi;
        }
      });
    });
    return mins;
  });

  function isMinCpi(groupId, g) {
    if (!g || g.n <= 5 || g.cpi == null) return false;
    const min = minCpiByGroup.value[groupId];
    return min != null && Math.abs(g.cpi - min) < 0.001;
  }

  // Modal state
  const modalOpen = ref(false);
  const editingGroupId = ref(null);

  const editingGroup = computed(() =>
    editingGroupId.value
      ? store.respondentGroups.find((g) => g.id === editingGroupId.value) ?? null
      : null,
  );

  function openAddModal() {
    editingGroupId.value = null;
    modalOpen.value = true;
  }

  function openEditModal(groupId) {
    editingGroupId.value = groupId;
    modalOpen.value = true;
  }

  function handleSave(groupData) {
    if (editingGroupId.value) {
      store.updateGroup(editingGroupId.value, groupData);
    } else {
      store.addGroup(groupData);
    }
  }

  function handleDelete() {
    if (editingGroupId.value) store.removeGroup(editingGroupId.value);
  }
</script>

<template>
  <div class="rounded-md border bg-white overflow-auto max-h-[calc(100vh-110px)] [&_td]:text-xs [&_th]:text-xs">
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead
            v-for="dim in dimColumns"
            :key="dim.key">
            {{ dim.label }}
          </TableHead>
          <TableHead class="text-right">Cost</TableHead>
          <TableHead
            v-for="header in groupHeaders"
            :key="header.id"
            class="text-right min-w-32 group/head">
            <div class="flex items-center justify-end gap-1">
              <div class="flex flex-col items-end gap-0.5 min-w-0">
                <span>{{ header.label }}</span>
                <span
                  v-if="targetName(header.target_id)"
                  class="text-muted-foreground font-normal leading-tight"
                  style="font-size: 10px">
                  {{ targetName(header.target_id) }}
                </span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                class="size-5 opacity-0 group-hover/head:opacity-100 transition-opacity shrink-0"
                @click="openEditModal(header.id)">
                <Pencil class="size-3" />
              </Button>
            </div>
          </TableHead>
          <TableHead class="w-10 text-center">
            <Button
              variant="ghost"
              size="icon"
              class="size-6"
              @click="openAddModal">
              <Plus class="size-3.5" />
            </Button>
          </TableHead>
        </TableRow>
      </TableHeader>

      <TableBody>
        <TableRow
          v-for="row in rows"
          :key="row.key"
          :class="{
            'border-t-2 font-semibold': row.isSpecial === 'total',
            'text-muted-foreground italic': row.isSpecial === 'unattributed',
          }">
          <TableCell
            v-for="(dim, i) in dimColumns"
            :key="dim.key"
            :class="row.isSpecial ? '' : 'font-medium'">
            <template v-if="row.isSpecial === 'total' && i === 0">TOTAL</template>
            <template v-else-if="row.isSpecial === 'unattributed' && i === 0">?</template>
            <template v-else-if="row.isSpecial">—</template>
            <DimensionThumbnail
              v-else-if="dimensionImage(dim.key, row.dims)"
              :image="dimensionImage(dim.key, row.dims)"
              :label="dimensionValue(dim.key, row.dims)" />
            <template v-else>{{ dimensionValue(dim.key, row.dims) }}</template>
          </TableCell>

          <TableCell class="text-right tabular-nums">
            <template v-if="row.cost != null">{{ Math.round(row.cost) }}€</template>
            <template v-else>—</template>
          </TableCell>

          <TableCell
            v-for="header in groupHeaders"
            :key="header.id"
            class="text-right tabular-nums"
            :class="!row.isSpecial && isMinCpi(header.id, row.groups?.[header.id]) ? 'bg-status-success-soft text-status-success-strong' : ''">
            <template v-if="row.groups?.[header.id]?.n > 0">
              <template v-if="row.groups[header.id].cpi != null">
                {{ row.groups[header.id].cpi.toFixed(1) }}€
              </template>
              <span class="text-muted-foreground text-xs ml-0.5">
                ({{ row.groups[header.id].n }})
              </span>
            </template>
          </TableCell>

          <TableCell />
        </TableRow>

        <TableEmpty
          v-if="rows.filter((r) => !r.isSpecial).length === 0"
          :colspan="totalCols">
          No data for this period
        </TableEmpty>
      </TableBody>
    </Table>
  </div>

  <GroupModal
    v-model:open="modalOpen"
    :group="editingGroup"
    @save="handleSave"
    @delete="handleDelete" />
</template>
